import { useState } from "react";
import { toast } from "sonner";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

export default function Dice() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [target, setTarget] = useState(50);
  const [direction, setDirection] = useState("over");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const winChance = direction === "over" ? 99.99 - Number(target) : Number(target);
  const mult = winChance > 0 ? (99 / winChance).toFixed(4) : "—";
  const pct = direction === "over" ? Number(target) : Number(target);

  const placeBet = async (betAmount, quiet = false) => {
    try {
      const { data } = await api.post("/games/dice", {
        amount: Number(betAmount),
        currency,
        target: Number(target),
        direction,
      });
      setLast(data.bet);
      setUser((u) => ({ ...u, balances: data.balances }));
      if (!quiet) {
        if (data.bet.result.won) toast.success(`+${formatAmount(data.bet.profit, currency)} ${currency}`);
        else toast.error(`Loss`);
      }
      return data.bet;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return null;
    }
  };

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    setBusy(true);
    await placeBet(amount);
    setBusy(false);
  };

  return (
    <GameShell title="Dice" subtitle="Choose over or under. Edge is yours to dial." accent="#FFC107"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={(a) => placeBet(a, true)}
          busy={busy}
          disabled={!user || busy}
          playLabel={`Roll ${direction} ${Number(target).toFixed(2)}`}
          extra={
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                  <span>Multiplier</span>
                  <span className="font-mono-num text-amber-400">×{mult}</span>
                </div>
                <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                  <span>Win Chance</span>
                  <span className="font-mono-num text-emerald-400">{winChance.toFixed(2)}%</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className={`flex-1 py-2 rounded-lg text-xs uppercase tracking-wider ${
                    direction === "over" ? "btn-cyan" : "btn-ghost"
                  }`}
                  onClick={() => setDirection("over")}
                  data-testid="dice-over"
                >
                  Over
                </button>
                <button
                  className={`flex-1 py-2 rounded-lg text-xs uppercase tracking-wider ${
                    direction === "under" ? "btn-cyan" : "btn-ghost"
                  }`}
                  onClick={() => setDirection("under")}
                  data-testid="dice-under"
                >
                  Under
                </button>
              </div>
            </div>
          }
        />
      }
    >
      <div className="h-[460px] flex flex-col items-center justify-center gap-10">
        <div className="font-display font-black text-7xl md:text-8xl tracking-tighter" data-testid="dice-roll">
          {last ? Number(last.result.roll).toFixed(2) : "—"}
        </div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          {last ? (last.result.won ? "Winner" : "Try Again") : "Pick a side, roll the fate"}
        </div>

        <div className="w-full max-w-xl">
          <div className="flex justify-between text-[10px] font-mono-num text-zinc-500 mb-2">
            <span>0.00</span>
            <span className="text-amber-400">Target {Number(target).toFixed(2)}</span>
            <span>99.99</span>
          </div>
          <input
            type="range"
            min="0.01"
            max="99.99"
            step="0.01"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="dice-slider"
            style={{ "--pct": `${pct}%` }}
            data-testid="dice-slider"
          />
          <div className="flex justify-between text-[10px] font-mono-num text-zinc-600 mt-2">
            <span className="text-rose-400">{direction === "under" ? "WIN" : "LOSE"}</span>
            <span className="text-emerald-400">{direction === "over" ? "WIN" : "LOSE"}</span>
          </div>
        </div>
      </div>
    </GameShell>
  );
}
